import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  TrendingUp,
  FileSearch,
  BarChart3,
  Layout,
  Cpu,
  PenTool,
  Rocket,
  Lightbulb,
  Search,
  Code2,
  Settings,
  Zap,
} from 'lucide-react';

import SEO from '../components/SEO';

const IndividuellePage = () => (
  <div className="pt-20">
    <SEO
      title="Individuelle KI-Lösungen – Referenzen & Kundenprojekte"
      description="Erfolgreich umgesetzte KI-Projekte: Finanzdienstleistungen, Leadgeneratoren, ERP-Systeme, Dokumentenautomatisierung. LAIzyones entwickelt individuelle Lösungen aus Magdeburg."
      path="/individuelle-loesungen"
      keywords="individuelle KI Lösungen, KI Referenzen, KI Projekte Unternehmen, ERP System entwickeln, Leadgenerator KI, Dokumentenautomatisierung, KI Finanzdienstleistungen, maßgeschneiderte Software"
      breadcrumbs={[
        { name: 'Startseite', url: '/' },
        { name: 'Individuelle Lösungen' },
      ]}
      serviceSchema={{
        name: 'Individuelle KI-Lösungen',
        description: 'Maßgeschneiderte KI-Lösungen für individuelle Herausforderungen: Von Finanzdienstleistungen über Leadgeneratoren bis zu ERP-Systemen.',
      }}
    />
    <HeroSection />
    <ProjectsSection />
    <ProcessSection />
    <CTASection />
  </div>
);

/* ───── Hero ───── */
const HeroSection = () => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="badge mb-6 fade-up">Referenzen & Kundenprojekte</div>
        <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 mb-6">
          Wir entwickeln Lösungen, <span className="gradient-text">wo andere an Grenzen stoßen</span>
        </h1>
        <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto mb-8 fade-up">
          Kein Projekt gleicht dem anderen. Deshalb entwickeln wir keine Standardprodukte,
          sondern maßgeschneiderte Lösungen, die exakt zu Ihren Anforderungen passen — von der
          ersten Idee bis zum produktiven Einsatz.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up">
          <Link to="/#kontakt" className="btn-primary text-base">
            <span>Projekt besprechen</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ───── Projects / Use Cases ───── */
const projects = [
  {
    icon: TrendingUp,
    tag: 'Finanzbranche',
    title: 'KI-gestützte Finanzdienstleistungen',
    problem: 'Ein Finanzdienstleister benötigte intelligente Auswertungen großer Datenmengen, um Risiken frühzeitig zu erkennen und Beratungsqualität zu steigern.',
    solution: 'Wir entwickelten ein KI-System, das Finanzdaten in Echtzeit analysiert, Muster erkennt und automatisierte Handlungsempfehlungen generiert.',
    result: 'Deutlich schnellere Entscheidungsfindung und höhere Beratungsqualität durch datengestützte Insights.',
  },
  {
    icon: BarChart3,
    tag: 'Vertrieb & Marketing',
    title: 'Intelligente Leadgeneratoren für den Vertrieb',
    problem: 'Der Vertrieb arbeitete mit unqualifizierten Kontaktlisten und verlor wertvolle Zeit mit aussichtslosen Leads.',
    solution: 'Wir bauten KI-gesteuerte Leadgeneratoren, die gezielt qualifizierte Kunden identifizieren, bewerten und dem Vertrieb priorisiert übergeben.',
    result: 'Signifikante Steigerung der Abschlussquote und messbar höhere Umsätze durch fokussierten Vertriebseinsatz.',
  },
  {
    icon: Layout,
    tag: 'Conversion-Optimierung',
    title: 'Landingpages mit überdurchschnittlicher Conversion',
    problem: 'Bestehende Webauftritte generierten zu wenig Anfragen und spiegelten nicht die Qualität des Unternehmens wider.',
    solution: 'Individuell konzipierte Landingpages mit psychologisch optimierter Nutzerführung, A/B-Testing und SEO-Strategie.',
    result: 'Vielfach höhere Conversion-Raten und ein professioneller digitaler Auftritt, der Vertrauen schafft.',
  },
  {
    icon: FileSearch,
    tag: 'Dokumentenautomatisierung',
    title: 'Automatisierte Analyse von Auftragsbestätigungen',
    problem: 'Auftragsbestätigungen und Dokumente wurden handschriftlich erfasst und manuell in Systeme übertragen — fehleranfällig und zeitintensiv.',
    solution: 'KI-basierte Dokumentenerkennung (OCR + NLP), die Auftragsbestätigungen automatisch einliest, klassifiziert und die relevanten Daten extrahiert.',
    result: 'Komplette Umstellung von handschriftlicher auf digitale Verarbeitung mit drastischer Fehlerreduktion und Zeitersparnis.',
  },
  {
    icon: Cpu,
    tag: 'Unternehmenssoftware',
    title: 'Eigenes ERP-System von Grund auf entwickelt',
    problem: 'Standard-ERP-Systeme passten nicht zu den spezifischen Abläufen des Unternehmens und verursachten Workarounds und Ineffizienz.',
    solution: 'Wir entwickelten ein vollständig individuelles ERP-System, das exakt auf die Geschäftsprozesse zugeschnitten ist — von Warenwirtschaft bis Controlling.',
    result: 'Ein System, das wie angegossen passt: keine Workarounds, keine unnötigen Module, maximale Effizienz.',
  },
  {
    icon: PenTool,
    tag: 'Technische Analyse',
    title: 'Einlesen und Analyse technischer Zeichnungen',
    problem: 'Technische Zeichnungen mussten manuell ausgewertet werden — ein zeitaufwändiger Prozess, der Expertenwissen erforderte.',
    solution: 'Computer-Vision-Lösung, die technische Zeichnungen automatisch einliest, Maße extrahiert und mit Vorgaben abgleicht.',
    result: 'Automatisierte Prüfung technischer Zeichnungen in Sekunden statt Stunden — präzise und skalierbar.',
  },
  {
    icon: Rocket,
    tag: 'Neuprojekte',
    title: 'Komplett neue Projekte von Grund auf realisiert',
    problem: 'Kunden kamen mit einer Vision, aber ohne technischen Umsetzungsplan — und brauchten einen Partner, der von Null startet.',
    solution: 'Von der Ideenvalidierung über Prototyping bis zum marktreifen Produkt: Wir begleiteten den gesamten Entwicklungsprozess.',
    result: 'Funktionierende Produkte, die heute im Markt bestehen — umgesetzt in wenigen Wochen statt Monaten.',
  },
  {
    icon: Lightbulb,
    tag: 'Innovation',
    title: 'Begleitung neuer Ideen und Innovationen',
    problem: 'Unternehmen hatten innovative Ideen, wussten aber nicht, ob und wie sie technisch umsetzbar sind.',
    solution: 'Strukturierte Ideenvalidierung, technische Machbarkeitsanalyse und schrittweise Umsetzung mit regelmäßigem Feedback.',
    result: 'Aus vagen Ideen wurden konkrete, marktfähige Lösungen — begleitet vom ersten Gespräch bis zum Launch.',
  },
];

const ProjectsSection = () => (
  <section className="section bg-white">
    <div className="container-custom">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <div className="badge mb-4 fade-up">Was wir bereits geschaffen haben</div>
        <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
          Echte Projekte, <span className="gradient-text">echte Ergebnisse</span>
        </h2>
        <p className="body-text text-base md:text-lg text-gray-600 fade-up">
          Jedes dieser Projekte begann mit einer individuellen Herausforderung — und endete
          mit einer Lösung, die echten Mehrwert schafft.
        </p>
      </div>

      <div className="space-y-6">
        {projects.map((p, i) => {
          const Icon = p.icon;
          return (
            <div
              key={i}
              data-testid={`project-card-${i}`}
              className="card animate-card overflow-hidden"
            >
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">
                {/* Icon + Tag */}
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-2">
                    <Icon className="w-7 h-7 text-emerald-600" />
                  </div>
                  <span className="inline-block text-xs font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                    {p.tag}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Space_Grotesk'] font-bold text-xl text-gray-900 mb-4">{p.title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">Herausforderung</span>
                      <p className="text-gray-600 text-sm leading-relaxed">{p.problem}</p>
                    </div>
                    <div>
                      <span className="block text-xs font-bold uppercase tracking-wider text-emerald-500 mb-1.5">Unsere Lösung</span>
                      <p className="text-gray-600 text-sm leading-relaxed">{p.solution}</p>
                    </div>
                    <div>
                      <span className="block text-xs font-bold uppercase tracking-wider text-cyan-500 mb-1.5">Ergebnis</span>
                      <p className="text-gray-600 text-sm leading-relaxed">{p.result}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ───── Process ───── */
const ProcessSection = () => {
  const steps = [
    { num: '01', icon: Search, title: 'Analyse', desc: 'Wir verstehen Ihre Herausforderung im Detail — Prozesse, Engpässe, Ziele und Rahmenbedingungen.' },
    { num: '02', icon: Lightbulb, title: 'Konzept & Strategie', desc: 'Wir erarbeiten einen klaren Umsetzungsplan mit technischer Architektur, Zeitrahmen und Meilensteinen.' },
    { num: '03', icon: Code2, title: 'Entwicklung & Umsetzung', desc: 'Agile Umsetzung mit regelmäßigem Feedback — Sie sehen jederzeit den Fortschritt und können Einfluss nehmen.' },
    { num: '04', icon: Settings, title: 'Optimierung & Skalierung', desc: 'Nach dem Launch optimieren wir kontinuierlich und skalieren die Lösung bei wachsenden Anforderungen.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unsere Arbeitsweise</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Wie individuelle Lösungen <span className="gradient-text">bei uns entstehen</span>
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Strukturiert, transparent und immer nah an Ihren Anforderungen.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card text-center animate-card relative">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-emerald-600" />
                </div>
                <span className="font-['Space_Grotesk'] font-bold text-2xl gradient-text mb-2 block">{s.num}</span>
                <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 text-emerald-300">
                    <ChevronRight className="w-5 h-5" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ───── CTA ───── */
const CTASection = () => (
  <section className="section bg-white pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0F14] to-[#111820] p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-4">
            Sie haben eine Idee oder <span className="text-emerald-400">Herausforderung</span>?
          </h2>
          <p className="text-gray-400 text-base md:text-lg mb-8">
            Erzählen Sie uns davon. Wir hören zu, denken mit und entwickeln Ihre individuelle Lösung —
            kostenlose Erstberatung, kein Risiko.
          </p>
          <Link to="/#kontakt" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all">
            Individuelle Lösung anfragen <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default IndividuellePage;
